/**
 * Dates and money for the Billing tab in the active language (DESIGN.md §17):
 * a period reads "30 Sep to 30 Oct 2026" (the year once, on its end) or
 * "9月30日至2026年10月30日", a day "30 Oct 2026", a time "30 Oct 2026, 14:30".
 * Money is the invoice's currency, never the org's (`billing-format.ts`).
 */
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime, formatDay, formatPeriodEnds, useDateFnsLocale } from '@/lib/format-dates';
import { formatMoney, formatUnitPrice } from '../lib/billing-format';
import type { BillPeriod } from '../lib/billing-view';

export interface BillingFormat {
  period: (period: BillPeriod) => string;
  day: (iso: string | null) => string;
  dateTime: (iso: string | null) => string;
  money: (cents: number, currency: string) => string;
  unitPrice: (cents: number, currency: string) => string;
}

export function useBillingFormat(): BillingFormat {
  const { t } = useTranslation('billing');
  const locale = useDateFnsLocale();
  const period = useCallback(
    (p: BillPeriod) => t('period', formatPeriodEnds(p.start, p.end, locale)),
    [t, locale],
  );
  const day = useCallback((iso: string | null) => formatDay(iso, locale), [locale]);
  const dateTime = useCallback((iso: string | null) => formatDateTime(iso, locale), [locale]);
  return useMemo(
    () => ({ period, day, dateTime, money: formatMoney, unitPrice: formatUnitPrice }),
    [period, day, dateTime],
  );
}
